import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MainLayout from '../../../components/Layout/MainLayout';
import { BackIcon } from '../../../components/Dashboard/DataCenterIcons';
import '../../../assets/styles/FDCUploadPage.css';
import '../../../assets/styles/RegularRDCPage.css';
import { commonService } from '../../../services/commonService';
import { ROUTES } from '../../../utils/constants';

interface EditData {
    id: string;
    fulfillment_type: boolean;
    summary: string;
    crm_id: string;
    deal_id: string;
    comment: string;
    criteria: string[];
    geography: string;
    industry: string;
    technologies: string;
    job_title: string;
    company_size: string;
    revenue_size: string;
    number_of_contacts: string;
    data_fields: string;
    other: string;
    project_tire: string;
    brand_name: string;
    record_type: string;
    refulfillment_id: string;
}

interface FDCUploadOption {
    projectid: string;
    summary: string;
}

const FDCEditPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const editData: any = (location.state as any)?.editData;
    const [form, setForm] = useState<EditData | null>(null);
    const [originalForm, setOriginalForm] = useState<EditData | null>(null);
    const [submitEnabled, setSubmitEnabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fdcOptions, setFdcOptions] = useState<FDCUploadOption[]>([]);
    const [fetchingOptions, setFetchingOptions] = useState(false);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);

    // Load incoming data
    useEffect(() => {
        if (editData) {
            const formatted: EditData = {
                ...editData,
                criteria: editData.criteria ? editData.criteria.split(",").map((a: string) => a.trim()) : [],
                fulfillment_type: editData.fulfillment_type === "Enrichment"
            };
            setForm(formatted);
            setOriginalForm(JSON.parse(JSON.stringify(formatted)));

            // If this is a refulfillment/credit_fulfillment, fetch options
            if ((formatted.record_type === 'refulfillment' || formatted.record_type === 'credit_fulfillment') && formatted.crm_id) {
                getFDCIDs(formatted.crm_id);
            }
        }
    }, [editData]);

    // Get FDC IDs for refulfillment
    const getFDCIDs = async (crmId: string) => {
        if (!crmId) {
            setFdcOptions([]);
            return;
        }

        try {
            setFetchingOptions(true);
            const payload = { crm_id: crmId };
            const response = await commonService.getProjectId(payload);

            if (response?.message === 'success') {
                setFdcOptions(response?.data || []);
            } else {
                setFdcOptions([]);
            }
        } catch (error) {
            console.error('Error fetching FDC IDs:', error);
            setFdcOptions([]);
        } finally {
            setFetchingOptions(false);
        }
    };

    // Handle CRM ID blur to fetch options for refulfillment/credit_fulfillment
    const handleCrmIdBlur = () => {
        if ((form?.record_type === 'refulfillment' || form?.record_type === 'credit_fulfillment') && form.crm_id) {
            getFDCIDs(form.crm_id);
        }
    };

    // Handle form changes
    const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        if (!form) return;
        const { name, value, type } = e.target;
        let val: any = type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

        const updated = { ...form, [name]: val };

        // If record type changes, handle refulfillment_id
        if (name === 'record_type') {
            if (value === 'fulfillment') {
                updated.refulfillment_id = '';
                updated.comment = '';
                setFdcOptions([]);
            } else if ((value === 'refulfillment' || value === 'credit_fulfillment') && updated.crm_id) {
                getFDCIDs(updated.crm_id);
            }
        }

        // If CRM ID changes for refulfillment/credit_fulfillment, fetch new options
        if (name === 'crm_id' && (form.record_type === 'refulfillment' || form.record_type === 'credit_fulfillment') && value) {
            getFDCIDs(value);
        }

        // If fulfillment type changes, clear enrichment fields
        if (name === 'fulfillment_type') {
            if (val) {
                updated.geography = '';
                updated.industry = '';
                updated.technologies = '';
                updated.job_title = '';
                updated.company_size = '';
                updated.revenue_size = '';
            }
        }

        setForm(updated);
        setSubmitEnabled(hasChanged(updated, originalForm!));
    };

    const hasChanged = (updated: EditData, original: EditData) =>
        JSON.stringify(updated) !== JSON.stringify(original);

    const handleCriteriaToggle = (val: string) => {
        if (!form) return;
        let updated = form.criteria.includes(val)
            ? form.criteria.filter(c => c !== val)
            : [...form.criteria, val];
        const newForm = { ...form, criteria: updated };
        setForm(newForm);
        setSubmitEnabled(hasChanged(newForm, originalForm!));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!form) return;

        // Create FormData object to match the upload page structure
        const formData = new FormData();

        // Append all fields to FormData
        formData.append("summary", form.summary || "");
        formData.append("crm_id", form.crm_id || "");
        formData.append("deal_id", form.deal_id || "");
        formData.append("comment", form.comment || "");
        formData.append("number_of_contacts", form.number_of_contacts || "");
        formData.append("data_fields", form.data_fields || "");
        formData.append("other", form.other || "");
        formData.append("project_tire", form.project_tire || "");
        formData.append("brand_name", form.brand_name || "");
        formData.append("record_type", form.record_type || "fulfillment");
        formData.append("refulfillment_id", form.refulfillment_id || "");

        // Handle fulfillment type (convert boolean to string)
        formData.append("fulfillment_type", form.fulfillment_type ? "Enrichment" : "");

        // Handle criteria array
        form.criteria.forEach(criteria => {
            formData.append("criteria[]", criteria);
        });

        // Append enrichment fields (disabled when fulfillment_type is true)
        if (!form.fulfillment_type) {
            formData.append("geography", form.geography || "");
            formData.append("industry", form.industry || "");
            formData.append("technologies", form.technologies || "");
            formData.append("job_title", form.job_title || "");
            formData.append("company_size", form.company_size || "");
            formData.append("revenue_size", form.revenue_size || "");
        } else {
            // If enrichment is checked, send empty values for these fields
            formData.append("geography", "");
            formData.append("industry", "");
            formData.append("technologies", "");
            formData.append("job_title", "");
            formData.append("company_size", "");
            formData.append("revenue_size", "");
        }

        // If your API expects JSON instead of FormData, use this:
        const jsonPayload = {
            id: form.id, // Make sure to include the ID for update
            summary: form.summary || "",
            crm_id: form.crm_id || "",
            deal_id: form.deal_id || "",
            comment: form.comment || "",
            number_of_contacts: form.number_of_contacts || "",
            data_fields: form.data_fields || "",
            other: form.other || "",
            project_tire: form.project_tire || "",
            brand_name: form.brand_name || "",
            record_type: form.record_type || "fulfillment",
            refulfillment_id: form.refulfillment_id || "",
            fulfillment_type: form.fulfillment_type ? "Enrichment" : "",
            criteria: form.criteria.join(", "), // Join array to string
            geography: !form.fulfillment_type ? (form.geography || "") : "",
            industry: !form.fulfillment_type ? (form.industry || "") : "",
            technologies: !form.fulfillment_type ? (form.technologies || "") : "",
            job_title: !form.fulfillment_type ? (form.job_title || "") : "",
            company_size: !form.fulfillment_type ? (form.company_size || "") : "",
            revenue_size: !form.fulfillment_type ? (form.revenue_size || "") : ""
        };

        try {
            setLoading(true);
            const response = await commonService.fdcUpdate(jsonPayload);
            setSubmitEnabled(false);
            setSuccessMsg(response.message || "✔ Updated successfully");
            setTimeout(() => setSuccessMsg(null), 5000);
            setTimeout(() => navigate(ROUTES.FDC_LIST), 5000);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    if (!form) return <MainLayout><div style={{ textAlign: 'center', padding: 24 }}>Loading...</div></MainLayout>;

    return (
        <MainLayout>
            <div className="fdc-upload-page">
                <div className="fdc-upload-header">
                    <button onClick={() => navigate(-1)} className="regular-rdc-back-button">
                        <BackIcon /> Back
                    </button>
                    <span className="fdc-upload-title">Edit FDC Project</span>
                </div>
                <div className="line"></div>

                <form className="fdc-upload-form" onSubmit={handleSubmit}>
                    <div className="form-container">

                        {/* --- Project Information --- */}
                        {/* <div className="form-section-title">Project Information</div> */}

                        {/* Task Title */}
                        <div className="form-row">
                            <div className="form-label">Task Title</div>
                            <div className="form-inputs">
                                <input
                                    name="summary"
                                    value={form.summary}
                                    onChange={onChange}
                                    placeholder="Enter project Summary"
                                    required
                                />
                            </div>
                        </div>

                        {/* CRM ID */}
                        <div className="form-row">
                            <div className="form-label">CRM ID</div>
                            <div className="form-inputs">
                                <input
                                    name="crm_id"
                                    value={form.crm_id}
                                    onChange={onChange}
                                    onBlur={handleCrmIdBlur}
                                    placeholder="Enter CRM ID"
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-label">Deal ID</div>
                            <div className="form-inputs">
                                <input
                                    name="deal_id"
                                    value={form.deal_id}
                                    onChange={onChange}
                                    placeholder="Enter Deal ID"
                                    required
                                />
                            </div>
                        </div>

                        {/* Project Type */}
                        <div className="form-row">
                            <div className="form-label">Project Type</div>
                            <div className="form-inputs">
                                <select
                                    name="record_type"
                                    value={form.record_type}
                                    onChange={onChange}
                                    required
                                >
                                    <option value="fulfillment">Fulfillment</option>
                                    <option value="refulfillment">Refulfillment</option>
                                    <option value="credit_fulfillment">Credit Fulfillment</option>
                                </select>
                            </div>
                        </div>

                        {/* FDC Upload ID (shown for refulfillment and credit_fulfillment) */}
                        {(form.record_type === "refulfillment" || form.record_type === "credit_fulfillment") && (
                            <div className="form-row">
                                <div className="form-label">FDC Upload ID</div>
                                <div className="form-inputs">
                                    <select
                                        name="refulfillment_id"
                                        value={form.refulfillment_id}
                                        onChange={onChange}
                                        required={form.record_type === 'refulfillment' || form.record_type === 'credit_fulfillment'}
                                    >
                                        <option value="">-- Select FDC Upload ID --</option>
                                        {fetchingOptions ? (
                                            <option value="" disabled>Loading options...</option>
                                        ) : (
                                            fdcOptions.map(option => (
                                                <option key={option.projectid} value={option.projectid}>
                                                    {option.projectid} ({option.summary})
                                                </option>
                                            ))
                                        )}
                                    </select>
                                </div>
                            </div>
                        )}

                        {(form.record_type === "refulfillment" || form.record_type === "credit_fulfillment") && (
                            <div className="form-row">
                                <div className="form-label">Comment</div>
                                <div className="form-inputs">
                                    <textarea
                                        rows={3}
                                        name="comment"
                                        value={form.comment}
                                        onChange={onChange}
                                        placeholder="Enter Comment"                                        
                                    />
                                </div>
                            </div>
                        )}

                        {/* Fulfillment Type */}
                        <div className="form-row">
                            <div className="form-label">Fulfillment Type</div>
                            <div className="form-inputs" style={{ flex: "none" }}>
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="fulfillment_type"
                                        checked={form.fulfillment_type}
                                        onChange={onChange}
                                    />
                                    <span>Enrichment</span>
                                </label>
                            </div>
                        </div>

                        {/* --- Target Criteria --- */}
                        {/* <div className="form-section-title">Target Criteria</div> */}

                        {/* List Type */}
                        <div className="form-row">
                            <div className="form-label">List Type</div>
                            <div className="form-inputs" style={{ flex: "none" }}>
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={form.criteria.includes("B2B")}
                                        onChange={() => handleCriteriaToggle("B2B")}
                                    />
                                    <span>B2B</span>
                                </label>
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={form.criteria.includes("B2C")}
                                        onChange={() => handleCriteriaToggle("B2C")}
                                    />
                                    <span>B2C</span>
                                </label>
                            </div>
                        </div>

                        {/* Geography */}
                        <div className="form-row">
                            <div className="form-label">Geography</div>
                            <div className="form-inputs">
                                <input
                                    name="geography"
                                    value={form.geography}
                                    onChange={onChange}
                                    placeholder="Enter Geography"
                                    disabled={form.fulfillment_type}
                                />
                            </div>
                        </div>

                        {/* Industry */}
                        <div className="form-row">
                            <div className="form-label">Industry</div>
                            <div className="form-inputs">
                                <input
                                    name="industry"
                                    value={form.industry}
                                    onChange={onChange}
                                    placeholder="Enter Industry"
                                    disabled={form.fulfillment_type}
                                />
                            </div>
                        </div>

                        {/* Technologies */}
                        <div className="form-row">
                            <div className="form-label">Technologies</div>
                            <div className="form-inputs">
                                <input
                                    name="technologies"
                                    value={form.technologies}
                                    onChange={onChange}
                                    placeholder="Enter Technologies"
                                    disabled={form.fulfillment_type}
                                />
                            </div>
                        </div>

                        {/* Job Title */}
                        <div className="form-row">
                            <div className="form-label">Job Title</div>
                            <div className="form-inputs">
                                <input
                                    name="job_title"
                                    value={form.job_title}
                                    onChange={onChange}
                                    placeholder="Enter Job Title"
                                    disabled={form.fulfillment_type}
                                />
                            </div>
                        </div>

                        {/* Company Size */}
                        <div className="form-row">
                            <div className="form-label">Company Size</div>
                            <div className="form-inputs">
                                <input
                                    name="company_size"
                                    value={form.company_size}
                                    onChange={onChange}
                                    placeholder="Enter Company Size"
                                    disabled={form.fulfillment_type}
                                />
                            </div>
                        </div>

                        {/* Revenue Size */}
                        <div className="form-row">
                            <div className="form-label">Revenue Size</div>
                            <div className="form-inputs">
                                <input
                                    name="revenue_size"
                                    value={form.revenue_size}
                                    onChange={onChange}
                                    placeholder="Enter Revenue Size"
                                    disabled={form.fulfillment_type}
                                />
                            </div>
                        </div>

                        {/* Number of Contacts */}
                        <div className="form-row">
                            <div className="form-label">Number of Contacts</div>
                            <div className="form-inputs">
                                <input
                                    name="number_of_contacts"
                                    value={form.number_of_contacts}
                                    onChange={onChange}
                                    placeholder="Enter Number of Contacts"
                                />
                            </div>
                        </div>

                        {/* --- Data Requirements --- */}
                        {/* <div className="form-section-title">Data Requirements</div> */}

                        {/* Data Fields */}
                        <div className="form-row">
                            <div className="form-label">Data Fields</div>
                            <div className="form-inputs">
                                <textarea
                                    rows={3}
                                    name="data_fields"
                                    value={form.data_fields}
                                    onChange={onChange}
                                    placeholder="Enter Data Fields"
                                    required
                                />
                            </div>
                        </div>

                        {/* Other Criteria */}
                        <div className="form-row">
                            <div className="form-label">Other Criteria</div>
                            <div className="form-inputs">
                                <textarea
                                    rows={3}
                                    name="other"
                                    value={form.other}
                                    onChange={onChange}
                                    placeholder="Enter other criteria"
                                />
                            </div>
                        </div>

                        {/* --- Project Settings --- */}
                        {/* <div className="form-section-title">Project Settings</div> */}

                        {/* Project Tier */}
                        <div className="form-row">
                            <div className="form-label">Project Tier</div>
                            <div className="form-inputs">
                                <input
                                    name="project_tire"
                                    value={form.project_tire}
                                    onChange={onChange}
                                    placeholder="Enter Project Tier"
                                    required
                                />
                            </div>
                        </div>

                        {/* Brand Name */}
                        <div className="form-row">
                            <div className="form-label">Brand Name</div>
                            <div className="form-inputs">
                                <select
                                    name="brand_name"
                                    value={form.brand_name}
                                    onChange={onChange}
                                    required
                                >
                                    <option value="">-- select --</option>
                                    <option value="DataCaptive">DataCaptive</option>
                                    <option value="ContactAffix">ContactAffix</option>
                                    <option value="ProDataLabs">ProDataLabs</option>
                                    <option value="VisitorsList">VisitorsList</option>
                                    <option value="TechDataPark">TechDataPark</option>
                                    <option value="CampaignLake">CampaignLake</option>
                                    <option value="ESalesClub">ESalesClub</option>
                                    <option value="InboxCEO">InboxCEO</option>
                                    <option value="MedicoLeads">MedicoLeads</option>
                                    <option value="ExpoCaptive">ExpoCaptive</option>
                                </select>
                            </div>
                        </div>

                        <div className="line"></div>

                        {/* Form Actions */}
                        <div className="form-actions">
                            <button
                                type="submit"
                                disabled={!submitEnabled || loading}
                                className={`submit-button ${submitEnabled ? 'button-enabled' : 'button-disabled'}`}
                            >
                                {loading ? "Updating..." : "Update Project"}
                            </button>
                            <div className="discard" onClick={() => navigate(-1)}>
                                Cancel
                            </div>
                        </div>

                    </div>
                </form>
            </div>

            {/* Success Toast Popup */}
            {successMsg && (
                <div style={{
                    position: "fixed",
                    top: "20px",
                    right: "20px",
                    backgroundColor: "#28a745",
                    color: "white",
                    padding: "12px 20px",
                    borderRadius: "8px",
                    boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
                    fontSize: "15px",
                    zIndex: 9999,
                    transition: "0.3s ease-in-out"
                }}>
                    {successMsg}
                </div>
            )}
        </MainLayout>
    );
};

export default FDCEditPage;